<template>
	<div v-if="!$root.sessionStore.has_posted_am_stats" class="am-stats-reminder">
		<p-message class="tight" :closable="false">
			<div class="flex align-items-center justify-content-between w-full">
				<div>Don't forget to post your daily stats!</div>
				<div>
					<p-button class="p-button-sm blue-button" @click="openModal()">Post Stats</p-button>
				</div>
			</div>
		</p-message>
		<p-dialog v-model:visible="show_stats_modal" modal style="max-width: 600px; width: 100%">
			<template #header>
				<div class="flex gap-2 align-content-center align-items-center">
					<icon type="chart-box-outline" size="24px" style="color: var(--color-b)" />
					<strong>Yesterday's Book Metrics ({{ yesterday }})</strong>
				</div>
			</template>
			<div class="stats">
				<div class="stat">
					<div class="stat-content">
						Active Accounts:
						<div class="stat-value">{{ percentage(stats.active_percent) }}</div>
					</div>
					<div class="stat-content">
						WoW Difference:
						<div class="stat-value">{{ currency(stats.total_spend_diff) }}</div>
						<div :class="['sub-value', trend(stats.total_spend_percent)]">
							({{ percentage(stats.total_spend_percent) }})
						</div>
					</div>
				</div>
				<div class="stat">
					<div class="stat-header">Calls</div>
					<div class="stat-content">
						<div class="stat-name">Largest WoW Increase&nbsp;-&nbsp;</div>
						<router-link
							:to="`/marketplaces/manage/${stats.largest_call_increase.account_id?.slice(0, 3)}/accounts/manage/${stats.largest_call_increase.account_id}/reports/account-activity`"
							target="_blank"
						>
							<strong>{{ stats.largest_call_increase.account_name }}</strong> </router-link
						>:
						<div class="stat-value">{{ currency(stats.largest_call_increase.increase) }}</div>
						<div :class="['sub-value', trend(stats.largest_call_increase.percent)]">
							({{ percentage(stats.largest_call_increase.percent) }})
						</div>
					</div>
					<div class="stat-content">
						<div class="stat-name">Largest WoW Decrease&nbsp;-&nbsp;</div>
						<router-link
							:to="`/marketplaces/manage/${stats.largest_call_decrease.account_id?.slice(0, 3)}/accounts/manage/${stats.largest_call_decrease.account_id}/reports/account-activity`"
							target="_blank"
						>
							<strong>{{ stats.largest_call_decrease.account_name }}</strong> </router-link
						>:
						<div class="stat-value">{{ currency(stats.largest_call_decrease.increase) }}</div>
						<div :class="['sub-value', trend(stats.largest_call_decrease.percent)]">
							({{ percentage(stats.largest_call_decrease.percent) }})
						</div>
					</div>
				</div>
				<div class="stat">
					<div class="stat-header">Exclusive Leads</div>
					<div class="stat-content">
						<div class="stat-name">Largest WoW Increase&nbsp;-&nbsp;</div>
						<router-link
							:to="`/marketplaces/manage/${stats.largest_exclusive_increase.account_id?.slice(0, 3)}/accounts/manage/${stats.largest_exclusive_increase.account_id}/reports/account-activity`"
							target="_blank"
						>
							<strong>{{ stats.largest_exclusive_increase.account_name }}</strong> </router-link
						>:
						<div class="stat-value">{{ currency(stats.largest_exclusive_increase.increase) }}</div>
						<div :class="['sub-value', trend(stats.largest_exclusive_increase.percent)]">
							({{ percentage(stats.largest_exclusive_increase.percent) }})
						</div>
					</div>
					<div class="stat-content">
						<div class="stat-name">Largest WoW Decrease&nbsp;-&nbsp;</div>
						<router-link
							:to="`/marketplaces/manage/${stats.largest_exclusive_decrease.account_id?.slice(0, 3)}/accounts/manage/${stats.largest_exclusive_decrease.account_id}/reports/account-activity`"
							target="_blank"
						>
							<strong>{{ stats.largest_exclusive_decrease.account_name }}</strong> </router-link
						>:
						<div class="stat-value">{{ currency(stats.largest_exclusive_decrease.increase) }}</div>
						<div :class="['sub-value', trend(stats.largest_exclusive_decrease.percent)]">
							({{ percentage(stats.largest_exclusive_decrease.percent) }})
						</div>
					</div>
				</div>
				<div class="stat">
					<div class="stat-header">Shared Leads</div>
					<div class="stat-content">
						<div class="stat-name">Largest WoW Increase&nbsp;-&nbsp;</div>
						<router-link
							:to="`/marketplaces/manage/${stats.largest_shared_increase.account_id?.slice(0, 3)}/accounts/manage/${stats.largest_shared_increase.account_id}/reports/account-activity`"
							target="_blank"
						>
							<strong>{{ stats.largest_shared_increase.account_name }}</strong> </router-link
						>:
						<div class="stat-value">{{ currency(stats.largest_shared_increase.increase) }}</div>
						<div :class="['sub-value', trend(stats.largest_shared_increase.percent)]">
							({{ percentage(stats.largest_shared_increase.percent) }})
						</div>
					</div>
					<div class="stat-content">
						<div class="stat-name">Largest WoW Decrease&nbsp;-&nbsp;</div>
						<router-link
							:to="`/marketplaces/manage/${stats.largest_shared_decrease.account_id?.slice(0, 3)}/accounts/manage/${stats.largest_shared_decrease.account_id}/reports/account-activity`"
							target="_blank"
						>
							<strong>{{ stats.largest_shared_decrease.account_name }}</strong> </router-link
						>:
						<div class="stat-value">{{ currency(stats.largest_shared_decrease.increase) }}</div>
						<div :class="['sub-value', trend(stats.largest_shared_decrease.percent)]">
							({{ percentage(stats.largest_shared_decrease.percent) }})
						</div>
					</div>
				</div>
				<div class="notes">
					<div class="control-group">
						<div class="inner">
							<label class="control-label" for="notes">Notes:</label>
							<div class="controls">
								<div class="field">
									<p-textarea id="notes" v-model="notes" class="p-inputtext w-full" style="min-height: 4rem" />
									<div v-if="v$.notes.$error" class="validation-error">
										{{ v$.notes.$errors[0].$message }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<template #footer>
				<p-button text @click="show_stats_modal = false">Cancel</p-button>
				<p-button @click="postDailyStats()" label="Post to Slack">
					<template #icon>
						<icon type="slack" size="20px" style="margin-right: 5px" />
					</template>
				</p-button>
			</template>
		</p-dialog>
	</div>
</template>

<script>
import dayjs from 'dayjs';
import { postDailyAMStats, reportAMDailyStats } from '@GQL';
import { useSessionStore } from '@/stores/session';
import { currency, numberFormat, title, percentage } from '@/lib/Filters';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

export default {
	name: 'AMStatsReminder',
	props: {},
	async setup() {
		return {
			sessionStore: useSessionStore(),
			v$: useVuelidate(),
		};
	},
	data() {
		return {
			show_stats_modal: true,
			yesterday: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
			stats: {},
			notes: '',
		};
	},
	methods: {
		title,
		currency,
		numberFormat,
		percentage,
		async openModal() {
			// Pull the daily stats
			await this.getData();
			this.v$.$reset();
			this.show_stats_modal = true;
		},
		async getData() {
			try {
				const result = await reportAMDailyStats(this.sessionStore.user.id);
				this.stats = result;
			} catch (error) {
				this.$toast.add({
					severity: 'error',
					summary: 'Error',
					detail: `Failed to fetch daily stats. ${error}`,
				});
			}
		},
		async postDailyStats() {
			try {
				const is_valid = await this.v$.$validate();

				if (is_valid) {
					// Post the daily stats to Slack
					await postDailyAMStats(this.sessionStore.user.id, this.notes);
					this.$root.sessionStore.has_posted_am_stats = true;

					this.$toast.add({
						severity: 'success',
						summary: 'Daily Stats Posted Successfully',
						life: 3000,
					});
				}
			} catch (err) {
				// API Library will handle this
			}
		},
		trend(value) {
			if (Math.abs(value) > 0.05) {
				if (value > 0) return 'trending-up';
				if (value < 0) return 'trending-down';
			}
			return '';
		},
	},
	validations() {
		return {
			notes: {
				required: helpers.withMessage('Notes are required to post daily metrics', required),
			},
		};
	},
};
</script>

<style lang="less" scoped>
.blue-button {
	background-color: var(--blue);

	&:hover {
		background-color: var(--blue);
	}
}

.p-message.tight {
	margin: 0;
}

.stats {
	font-size: 0.875rem;

	.stat {
		padding: 0.5rem 0;
	}

	.stat-header {
		font-weight: bold;
		font-size: 1rem;
		margin: 0.5rem 0;
	}

	.stat-content {
		display: flex;
		align-items: center;
		padding: 0.25rem 0;

		.stat-value {
			font-weight: bold;
			margin: 0;
			padding-left: 0.25rem;
		}

		.sub-value {
			margin: 0;
			padding-left: 0.25rem;
		}
	}

	.notes {
		margin-top: 1rem;
	}
}
</style>
